/**
 * LiveTrackingMap — Admin / PM live view of all active driver tracking sessions.
 * Shows:
 *  - real-time driver position (gold pulse marker)
 *  - breadcrumb trail (gold polyline) actually driven
 *  - planned driving directions (charcoal dashed) via OSRM proxy
 *  - stop markers color-coded by visit status
 *
 * Auto-refreshes every 15 seconds.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Activity, Truck, RefreshCw } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 15000;
const DEFAULT_CENTER = [32.9, -96.95];
const DEFAULT_ZOOM = 9;

const STATUS_COLOR = {
  pending: '#71717a',
  arrived: '#FFD60A',
  completed: '#10b981',
};

// Truck SVG marker for the live driver position
const truckIcon = L.divIcon({
  className: 'live-driver-marker',
  html: `<div style="position:relative;">
           <div style="position:absolute;inset:-6px;border-radius:9999px;background:#FFD60A;opacity:.35;animation:necs-pulse 1.6s ease-out infinite;"></div>
           <div style="position:relative;width:28px;height:28px;border-radius:9999px;background:#FFD60A;border:2px solid #0a0a0a;display:flex;align-items:center;justify-content:center;font-size:14px;">🚚</div>
         </div>
         <style>@keyframes necs-pulse{0%{transform:scale(1);opacity:.6}100%{transform:scale(2.2);opacity:0}}</style>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

export default function LiveTrackingMap() {
  const [sessions, setSessions] = useState([]);
  const [details, setDetails] = useState({}); // sid -> full session w/ breadcrumbs
  const [directions, setDirections] = useState({}); // sid -> [[lat,lng]]
  const [selectedSid, setSelectedSid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const timerRef = useRef(null);

  const loadActive = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tracking/active`, { credentials: 'include' });
      if (!res.ok) return;
      const list = await res.json();
      setSessions(list);
      // Auto-select first if none
      if (!selectedSid && list.length) setSelectedSid(list[0].session_id);
      // Refresh details for currently selected
      const sid = selectedSid || list[0]?.session_id;
      if (sid) await loadDetails(sid);
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (sid) => {
    try {
      const res = await fetch(`${API}/api/tracking/session/${sid}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setDetails((prev) => ({ ...prev, [sid]: data }));
      // Lazy-load planned driving polyline once per session
      const stops = (data.stops || []).filter((s) => s.latitude && s.longitude);
      if (stops.length >= 2 && !directions[sid]) {
        try {
          const coords = stops.map((s) => `${s.longitude},${s.latitude}`).join(';');
          const dres = await fetch(`${API}/api/tracking/directions?coords=${encodeURIComponent(coords)}`, {
            credentials: 'include',
          });
          if (dres.ok) {
            const d = await dres.json();
            setDirections((prev) => ({ ...prev, [sid]: d.geometry || [] }));
          }
        } catch (_) { /* OSRM optional */ }
      }
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    loadActive();
    timerRef.current = setInterval(loadActive, REFRESH_MS);
    return () => clearInterval(timerRef.current);
     
  }, []);

  useEffect(() => {
    if (selectedSid) loadDetails(selectedSid);
     
  }, [selectedSid]);

  const selected = selectedSid ? details[selectedSid] : null;

  const breadcrumbsLine = useMemo(() => {
    if (!selected) return [];
    return (selected.breadcrumbs || []).map((b) => [b.lat, b.lng]);
  }, [selected]);

  const driverPos = useMemo(() => {
    if (!selected?.current_location) return null;
    return [selected.current_location.lat, selected.current_location.lng];
  }, [selected]);

  const center = driverPos || (selected?.stops?.find((s) => s.latitude)
    ? [selected.stops.find((s) => s.latitude).latitude, selected.stops.find((s) => s.latitude).longitude]
    : DEFAULT_CENTER);

  return (
    <Card className="bg-zinc-900 border-2 border-emerald-500/40 p-4" data-testid="live-tracking-map">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Activity className="h-5 w-5 text-emerald-400 animate-pulse" /> LIVE PICKUP TRACKING
          </h3>
          <p className="text-[11px] text-zinc-400 mt-1">
            Real-time driver positions, breadcrumb trails, and planned driving routes.
            {lastUpdate && <span className="ml-2 text-zinc-500">Updated {lastUpdate.toLocaleTimeString()}</span>}
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={loadActive}
          disabled={loading}
          className="border-emerald-500 text-emerald-400"
          data-testid="live-tracking-refresh-btn"
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {sessions.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 text-sm" data-testid="live-tracking-empty">
          No active driver sessions right now. When a supervisor or crew leader starts a route in the Staff app, it will appear here in real time.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Active sessions list */}
          <div className="space-y-2 lg:col-span-1" data-testid="live-tracking-sessions">
            {sessions.map((s) => {
              const isSel = s.session_id === selectedSid;
              const completedStops = (s.stops || []).filter((x) => x.status === 'completed').length;
              const totalStops = (s.stops || []).length;
              return (
                <button
                  key={s.session_id}
                  onClick={() => setSelectedSid(s.session_id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${isSel ? 'bg-emerald-900/40 border-emerald-500' : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'}`}
                  data-testid={`live-session-${s.session_id}`}
                >
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-[#FFD60A]" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-white truncate">{s.driver_name}</div>
                      <div className="text-[10px] text-zinc-400 truncate">
                        {s.driver_badge || s.driver_role} · {s.route_name}
                      </div>
                    </div>
                    <Badge className="bg-emerald-600 text-white text-[10px]">LIVE</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-300">
                    <span>{completedStops}/{totalStops} stops</span>
                    <div className="flex-1 h-1.5 bg-zinc-700 rounded overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: totalStops ? `${(completedStops / totalStops) * 100}%` : '0%' }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Map + stops */}
          <div className="lg:col-span-3 space-y-3">
            <div style={{ height: 420 }} className="rounded-lg overflow-hidden border border-zinc-700">
              <MapContainer center={center} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {/* Planned driving directions (dashed) */}
                {selectedSid && (directions[selectedSid] || []).length > 1 && (
                  <Polyline
                    positions={directions[selectedSid]}
                    pathOptions={{ color: '#3D3D3D', weight: 4, opacity: 0.55, dashArray: '8 6' }}
                  />
                )}
                {/* Actual breadcrumb trail */}
                {breadcrumbsLine.length > 1 && (
                  <Polyline
                    positions={breadcrumbsLine}
                    pathOptions={{ color: '#FFD60A', weight: 5, opacity: 0.95 }}
                  />
                )}
                {/* Stops */}
                {(selected?.stops || []).filter((s) => s.latitude && s.longitude).map((s, idx) => (
                  <CircleMarker
                    key={s.property_id}
                    center={[s.latitude, s.longitude]}
                    radius={11}
                    pathOptions={{
                      color: '#0a0a0a', weight: 1.5,
                      fillColor: STATUS_COLOR[s.status] || '#71717a', fillOpacity: 0.95,
                    }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold">#{idx + 1} · {s.property_name}</div>
                        <div className="text-zinc-700">{s.address}</div>
                        <div className="mt-1 capitalize">Status: <strong>{s.status}</strong></div>
                        {s.arrived_at && <div className="text-[10px] text-zinc-600">Arrived: {new Date(s.arrived_at).toLocaleTimeString()}</div>}
                        {s.completed_at && <div className="text-[10px] text-zinc-600">Completed: {new Date(s.completed_at).toLocaleTimeString()}</div>}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
                {/* Live driver */}
                {driverPos && (
                  <Marker position={driverPos} icon={truckIcon}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold">{selected.driver_name}</div>
                        <div className="text-zinc-700">{selected.route_name}</div>
                        <div className="text-[10px] text-zinc-600 mt-1">
                          Last ping: {new Date(selected.current_location.ts).toLocaleTimeString()}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>

            {/* Stops list */}
            {selected && (
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3" data-testid="live-tracking-stops">
                <div className="text-xs font-bold text-white mb-2">Stops on this route</div>
                <ol className="space-y-1.5">
                  {(selected.stops || []).map((s, i) => (
                    <li key={s.property_id} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLOR[s.status] || '#71717a' }} />
                      <span className="text-zinc-400 w-5 text-right">{i + 1}.</span>
                      <span className="text-white truncate flex-1">{s.property_name}</span>
                      <Badge className={
                        s.status === 'completed' ? 'bg-emerald-600 text-white' :
                        s.status === 'arrived' ? 'bg-[#FFD60A] text-black' :
                        'bg-zinc-700 text-zinc-300'
                      }>{s.status}</Badge>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
